import React, { useState, useEffect, useRef } from "react";
import apiService from "../config";
import { Download, Printer, Upload, Edit } from 'lucide-react';
import { useLanguage } from '../StoreContext/LanguageContext';
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';

const EditProposal = ({ projectName, proposalId, onBack }) => {
  const { language, translations } = useLanguage();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [signatureFile, setSignatureFile] = useState(null);
  const [areaUnits, setAreaUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  
  const {
    uploadStatus,
    selectedSignDocument,
    resetSignatureState,
    signDocument
  } = useSignature();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSignAvailable, setIsSignAvailable] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfForSigning, setPdfForSigning] = useState(null);
  const [viewerFile, setViewerFile] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  
  // 🔥 This makes preview show ONLY ONE TIME
  const hasShownPreviewOnce = useRef(false);

  // Load existing proposal data
  useEffect(() => {
    if (proposalId) {
      loadProposalData();
    }
  }, [proposalId]);

  useEffect(() => {
    // Cleanup preview URL when component unmounts or file changes
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadProposalData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/getProposal?proposalId=${proposalId}`);
      if (response && response.status === 200) {
        let data = response.data || {};

        // Fields that should be shown as "हो" / "नाही"
        const fieldsToConvert = [
          "schedule_6_08",
          "schedule_4_09",
          "extractsextracts_7_12_10",
          "drone_kml",
          "demarcation_11",
          "acquisition_matter_12",
          "land_acquisition_law_13",
          "taba_14",
          "other_govt_land_15",
          "near_railway_boundaries_16",
          "religious_places_17",
          "any_obstruction_18",
          "road_obstruction_19",
          "private_buildings_20",
          "expenses_22",
          "financial_provision_23",
          "acquisition_permanent_temporary"
        ];

        // Convert 1/0, true/false to "हो"/"नाही"
        fieldsToConvert.forEach(field => {
          if (data[field] === 1 || data[field] === true || data[field] === "1") {
            data[field] = "होय";
          } else if (data[field] === 0 || data[field] === false || data[field] === "0") {
            data[field] = "नाही";
          }
        });

        setFormData(prev => ({
          ...data,
          acquisition_proposal_no:
            data?.acquisition_proposal_no ||
            `${projectName || 'N/A'} साठी खाजगी जमिनीच्या भूसंपादचा प्रस्ताव`
        }));
      }
    } catch (err) {
      console.error("Error loading proposal data:", err);
      setError("Failed to load proposal data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      let allowedTypes;
      let allowedExtensions;
      let sizeLimit;
      let fileTypeDescription;

      // Check if this is the KML field
      if (fieldName === "drone_kml") {
        allowedTypes = [
          "video/mp4",
          "application/vnd.google-earth.kml+xml",
          "application/vnd.google-earth.kmz",
          "application/xml"  // Some systems send KML as generic XML
        ];
        allowedExtensions = ["mp4", "kml", "kmz"];
        fileTypeDescription = "MP4 व्हिडिओ किंवा KML/KMZ फाइल";
      } else {
        // All other fields accept the same file types
        allowedTypes = [
          "video/mp4",
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/jpeg",
          "image/jpg",
          "image/png"
        ];
        allowedExtensions = ["mp4", "pdf", "doc", "docx", "png", "jpg", "jpeg", "kml", "kmz"];
        fileTypeDescription = "MP4, PDF, Word Document, PNG, JPG किंवा JPEG";
      }

      // Get file extension
      const fileExtension = file.name.toLowerCase().split('.').pop();

      // Set size limit based on file type
      if (fileExtension === "mp4") {
        sizeLimit = 20 * 1024 * 1024; // 20 MB for videos
      } else if (fileExtension === "kml" || fileExtension === "kmz") {
        sizeLimit = 5 * 1024 * 1024; // 5 MB for KML files
      } else {
        sizeLimit = 5 * 1024 * 1024; // 5 MB for other files
      }

      // Validate file extension
      if (!allowedExtensions.includes(fileExtension)) {
        alert(`कृपया फक्त ${fileTypeDescription} फाइल अपलोड करा`);
        return;
      }

      // Validate MIME type (with fallback for extension-based validation)
      const isValidMimeType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (!isValidMimeType && !isValidExtension) {
        alert(`कृपया फक्त ${fileTypeDescription} फाइल अपलोड करा`);
        return;
      }

      // Validate file size
      if (file.size > sizeLimit) {
        const sizeLimitMB = fileExtension === "mp4" ? "20MB" : "5MB";
        alert(`फाइल साइझ ${sizeLimitMB} पेक्षा कमी असावी`);
        return;
      }

      // Additional validation: Empty file check
      if (file.size === 0) {
        alert("रिकामी फाइल अपलोड करू शकत नाही");
        return;
      }

      // Specific file type validations
      if (fileExtension === "pdf" && file.type !== "application/pdf") {
        alert("अपलोड केलेली फाइल वैध PDF नाही");
        return;
      }

      if (["png", "jpg", "jpeg"].includes(fileExtension) && !file.type.startsWith("image/")) {
        alert("अपलोड केलेली फाइल वैध इमेज नाही");
        return;
      }

      if (["doc", "docx"].includes(fileExtension) &&
        !["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(file.type)) {
        console.warn("Word document MIME type validation skipped due to browser compatibility");
      }

      if (fileExtension === "mp4" && file.type !== "video/mp4") {
        alert("अपलोड केलेली फाइल वैध MP4 व्हिडिओ नाही");
        return;
      }

      // KML/KMZ validation
      if ((fileExtension === "kml" || fileExtension === "kmz") &&
        !["application/vnd.google-earth.kml+xml", "application/vnd.google-earth.kmz", "application/xml"].includes(file.type)) {
        console.warn("KML file MIME type validation relaxed due to browser compatibility");
      }

      // Store the file with the correct field name
      const fileFieldName = `${fieldName}_file`;
      setFormData((prev) => ({
        ...prev,
        [fileFieldName]: file,
      }));

      console.log(`✓ File uploaded successfully for field: ${fieldName}`, file.name);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear success message when user starts editing
    if (success) setSuccess(false);
  };

  const handleViewFile = (fieldName) => {
    const fileUrl = formData[`${fieldName}_file`];
    console.log("🔎 handleViewFile called for:", fieldName, "=>", fileUrl);

    if (!fileUrl) {
      console.warn("⚠️ No file found for field:", fieldName);
      return;
    }

    // Detect File object vs URL string
    if (fileUrl instanceof File) {
      console.log("📂 Detected a File object:", fileUrl.name);
      const url = URL.createObjectURL(fileUrl);
      console.log("👉 Created blob URL:", url);
      setDocumentUrl(url);
    } else {
      console.log("🌐 Detected a string URL:", fileUrl);
      setDocumentUrl(fileUrl);
    }

    setShowModal(true);
    console.log("✅ Modal state set to true");
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    // Extract only date components, ignore time
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // Handle ISO date format from database (2025-08-01T00:00:00.000Z)
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      if (day && month && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle DD-MM-YYYY format
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[2].length === 4) {
        // DD-MM-YYYY format
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else if (parts.length === 3 && parts[0].length === 4) {
        // Already in YYYY-MM-DD format
        return dateString;
      }
    }

    return '';
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (optional)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('कृपया फक्त JPG, PNG किंवा PDF फाइल अपलोड करा');
        return;
      }

      // Validate file size (optional - 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('फाइल साइझ 5MB पेक्षा कमी असावी');
        return;
      }

      // Set the signature file
      setSignatureFile(file);

      // Create preview URL for images (not for PDFs)
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl(null);
      }

      console.log('Signature file uploaded:', file.name);
    }
  };


  // Updated formatDateForDatabase function
  const formatDateForDatabase = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  const handleUploadedFile = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // ✅ open uploaded file in signature modal
    setPdfForSigning(file);
    setShowSignaturePad(true);
  };

  const handleViewSignedPdf = async () => {
    const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
    const projectId = selectedProject?.id || selectedProject?.project_id;
    const proposalId = localStorage.getItem("currentProposalId");

    const res = await apiService.getSectionFile(
      projectId,
      proposalId,
      "namukoK"
    );

    if (res?.isSignAvailable && res.data?.[0]?.file_path) {
      setViewerFile(res.data[0].file_path);
      setIsViewerOpen(true);
    } else {
      alert("Signed document not available");
    }
  };

  // Load Area Units
  const loadAreaUnits = async () => {
    try {
      const response = await apiService.getAreaUnits();
      if (response && response.status === 200) {
        setAreaUnits(response.data || []);
      }
    } catch (error) {
      console.error('Error loading area units:', error);
      setError('क्षेत्रफळ युनिट लोड करण्यात अयशस्वी');
    }
  };

  useEffect(() => {
    loadAreaUnits();
  }, []);

  useEffect(() => {
    if (!selectedSignDocument) return;

    const postSignedPdf = async () => {
      try {
        const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
        const projectId = selectedProject?.id || selectedProject?.project_id;
        const proposalId = localStorage.getItem("currentProposalId");

        await apiService.postAddSignaturePDF(
          projectId,
          proposalId,
          "namukoK", // 🔑 section key for EditProposal
          selectedSignDocument
        );

        const res = await apiService.getSectionFile(
          projectId,
          proposalId,
          "namukoK"
        );

        if (
          res?.isSignAvailable &&
          res.data?.[0]?.file_path &&
          !hasShownPreviewOnce.current
        ) {
          setPreviewUrl(res.data[0].file_path); // 👈 show preview ONCE
          hasShownPreviewOnce.current = true;
        }

        setIsSignAvailable(res?.isSignAvailable);
      } catch (err) {
        console.error(err);
      } finally {
        resetSignatureState();
      }
    };

    postSignedPdf();
  }, [selectedSignDocument]);

  const getSrNo = (idx) => {
    // Handles Sr. No. 1-9
    if (idx < 9) {
      return idx + 1;
    }
    // Handles Sr. No. 10 for questions 10a and 10b
    if (idx === 9 || idx === 10) {
      return 10;
    }
    // Handles Sr. No. 11-16
    if (idx > 10 && idx < 17) {
      return idx;
    }
    // Handles Sr. No. 17 for questions 17a and 17b
    if (idx === 17 || idx === 18) {
      return 17;
    }
    // Handles all subsequent Sr. Nos. (18 onwards)
    if (idx > 18) {
      return idx - 1;
    }
    // Default case (should not be reached with your form structure)
    return idx;
  };

  const handleSubmit = async () => {
    // ✅ Validation for compulsory file fields
    const bothFields = ["acquisition_matter_12", "religious_places_17"];
    for (const fieldName of bothFields) {
      const selectedValue = formData[fieldName];
      const hasFile = formData[`${fieldName}_file`];

      if ((selectedValue === "होय" || selectedValue === "नाही") && !hasFile) {
        const labelMap = {
          acquisition_matter_12: "क्र.12 (संपादनाबाबतचे प्रमाणपत्र)",
          religious_places_17: "क्र.17 (धार्मिक स्थळे)"
        };
        alert(`${labelMap[fieldName]} - कागदपत्र अपलोड करणे अनिवार्य आहे!`);
        setSaving(false);
        return; // ❌ Stop save
      }
    }
    setSaving(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      const convertToBitValue = (value) => {
        if (value === "होय") return "1";
        if (value === "नाही") return "0";
        return value;
      };

      const fieldsToConvert = [
        "schedule_6_08",
        "schedule_4_09",
        "extractsextracts_7_12_10",
        "demarcation_11",
        "acquisition_matter_12",
        "land_acquisition_law_13",
        "taba_14",
        "other_govt_land_15",
        "near_railway_boundaries_16",
        "religious_places_17",
        "any_obstruction_18",
        "road_obstruction_19",
        "private_buildings_20",
        "expenses_22",
        "financial_provision_23"
      ];

      const dateFields = [
        "acquisition_proposal_date",
        "administrative_order_date"
      ];

      const allowedFields = [
        "acquisition_proposal_no",
        "acquisition_proposal_date",
        "propsal_description",
        "administrative_order_no",
        "administrative_order_date",
        "acquisition_incharge_name",
        "acquisition_incharge_designation",
        "acquisition_agency",
        "acquisition_department",
        "acquisition_purpose",
        "acquisition_proposal_no",
        "acquisition_government_policy",
        "village_name",
        "taluka_name",
        "dist_name",
        "area_unit",
        "total_area",
        "aquired_area",
        "acquisition_regional_incharge_name",
        "acquisition_regional_incharge_designation",
        "acquisition_regional_incharge_email",
        "acquisition_regional_incharge_contact",
        "project_description",
        "project_social_benfit",
        "displacement_reason",
        "social_impact",
        ...fieldsToConvert
      ];


      Object.keys(formData).forEach(key => {
        if (allowedFields.includes(key) || key.endsWith('_file')) {
          if (key.endsWith('_file')) {
            // Only send if it's a newly uploaded File object
            if (formData[key] instanceof File) {
              console.log(`Attaching new file → ${key}: ${formData[key].name}`);
              formDataToSend.append(key, formData[key]);
            }
            // If it's a string (existing file URL/path) → do NOT append
          } else {
            let value = formData[key];

            if (dateFields.includes(key)) {
              value = formatDateForDatabase(value);
              if (value === null) return;
            }
            else if (fieldsToConvert.includes(key)) {
              value = convertToBitValue(value);
            }

            formDataToSend.append(key, value || '');
          }
        }
      });
      // Add uploaded files
      Object.keys(uploadedFiles).forEach(key => {
        formDataToSend.append(key, uploadedFiles[key]);
      });

      console.log('Updating proposal with fields:', Array.from(formDataToSend.keys()));

      const response = await apiService.patch(
        `/patchUpdateProposal?proposalId=${proposalId}`,
        formDataToSend
      );

      if (response && response.status === 200) {
        setSuccess(true);
        alert("प्रस्ताव सफळतापूर्वक अद्यतन केला गेला!");
        // loadProposalData();
        // window.location.reload();
      } else {
        setError(response?.message || "Update failed");
      }
    } catch (err) {
      console.error("Error updating proposal:", err);
      setError(err.message || "Failed to update proposal");
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderSave = async () => {
    try {
      if (!proposalId) {
        alert("Proposal ID not found");
        return;
      }

      setHeaderSaving(true);

      const formDataToSend = new FormData();
      formDataToSend.append("acquisition_proposal_no", formData.acquisition_proposal_no || "");

      const response = await apiService.patch(
        `/patchUpdateProposal?proposalId=${proposalId}`,
        formDataToSend
      );

      if (response?.status === 200 || response?.status === 201) {
        alert("Header updated successfully!");
        setIsEditingHeader(false);
      } else {
        alert("Failed to update header");
      }
    } catch (error) {
      console.error("Error updating header:", error);
      alert("Error while updating header");
    } finally {
      setHeaderSaving(false);
    }
  };

  const getFormattedDtv = (dtvArray) => {
    if (!dtvArray || !Array.isArray(dtvArray) || dtvArray.length === 0) return [];

    // Group by District -> Taluka
    const grouped = dtvArray.reduce((acc, curr) => {
      const dist = curr.dist_name || 'N/A';
      const taluka = curr.taluka_name || 'N/A';
      const village = curr.village_name || 'N/A';

      if (!acc[dist]) acc[dist] = {};
      if (!acc[dist][taluka]) acc[dist][taluka] = [];
      if (!acc[dist][taluka].includes(village)) {
        acc[dist][taluka].push(village);
      }
      return acc;
    }, {});

    // Convert grouped object to array for rendering
    const result = [];
    Object.entries(grouped).forEach(([dist, talukas]) => {
      Object.entries(talukas).forEach(([taluka, villages]) => {
        result.push({
          district: dist,
          taluka: taluka,
          villages: villages.join(", ") // Comma separated villages
        });
      });
    });
    return result;
  };

  const handleDownload = () => {
    const printWindow = window.open('', '_blank');

    // Create print content matching the image format
    const createPrintContent = () => {
      return `
        <div class="font-sans pt-4">
            <div class="text-center mb-5">
                <h2 class="mb-2 text-base font-bold">
                    नमुना "क"
                </h2>
                <p class="text-xs m-0 leading-tight mb-5 text-center">
                प्रकल्प नाव: ${projectName || 'N/A'} <br />
                </p>
                <p class="text-xs m-0 leading-tight mb-5 text-center">
                प्रस्ताव नाव :  ${formData?.acquisition_proposal_no }
                </p>
            </div>
            
            <table class="w-full border-collapse border-2 border-black text-xs">
                <tbody>
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">
                            1
                        </td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            भूसंपादन प्रस्ताव पाठविणाऱ्या अधिका-याचे नाव,पदनाम व पत्ता
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">नाव: </span>
                                    <span class="break-words">${formData.acquisition_incharge_name || "N/A"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">पदनाम/संपादन संस्थेचे नाव: </span>
                                    <span class="break-words">${formData.acquisition_incharge_designation || "-"}</span>
                                </div>
                                  <div>
                                    <span class="font-semibold">पत्ता: </span>
                                    <span class="break-words">${formData.acquisition_incharge_address || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">2</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            भूसंपादन प्रस्ताव सादर करणाऱ्या प्रादेशिक विभागाचे / प्राधिकरणाचे / महामंडळाचे नाव व पत्ता
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_agency || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">3</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            मंत्रालयीन स्तरावरील संबंधीत प्रशासकीय विभाग
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_department || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">4</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           जमीन कोणत्या कारणासाठी संपादन करून हवी आहे ?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_purpose || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">5</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                      जमीन कोणत्या शासन धोरणानुसार/कायद्यानुसार संपादन करून हवी आहे ?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.acquisition_government_policy || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">6</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करावयाच्या जमिनी ज्या क्षेत्रात मोडतात ते गाव, तालुका, जिल्हा.
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                ${formData.dtv && formData.dtv.length > 0 
                                  ? getFormattedDtv(formData.dtv).map(g => `
                                      <div style="margin-bottom: 8px; padding-bottom: 4px;">
                                          <div><span class="font-semibold">जिल्हा: </span><span class="break-words">${g.district}</span></div>
                                          <div><span class="font-semibold">तालुका: </span><span class="break-words">${g.taluka}</span></div>
                                          <div><span class="font-semibold">गाव: </span><span class="break-words">${g.villages}</span></div>
                                      </div>
                                    `).join('')
                                  : `<div><span class="break-words">-</span></div>`
                                }
                            </div>
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">7</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करून हवे असलेले एकूण क्षेत्र
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">एकूण क्षेत्र: </span>
                                    <span class="break-words">${formData.total_area || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">संपादन क्षेत्र: </span>
                                    <span class="break-words">${formData.aquired_area || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">क्षेत्रफळ युनिट: </span>
                                    <span class="break-words">${formData.area_unit || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">8</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        गावनिहाय व गट/सर्व्हे क्र. नुसार आणि संपादन क्षेत्राच्या चतुःसीमा दाखविणारी अनुसूची यासोबत जोडणे आवश्यक आहे, त्याप्रमाणे ती जोडली आहे काय ?(परिशिष्ट 6)
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.schedule_6_08}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">9</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        संपादन करावयाच्या क्षेत्राचा चिन्हांकित / साक्षांकित नकाशा प्रस्तावासोबत  जोडणे आवश्यक आहे, त्या प्रमाणे जोडला आहे काय ? भूमिअभिलेख विभागाकडील गावाचे नकाशे दोन प्रतीत सादर केले आहे काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.schedule_4_09}
                        </td>
                    </tr>

                  <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center" rowspan="2">10</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        अ. संपादन करावयाच्या क्षेत्राचे 7/12 चे उतारे किंवा मालमत्ता नोंदवहीतील उतारा प्रस्तावासोबत जोडणे आवश्यक आहे, त्याप्रमाणे जोडण्यात आलेले आहेत काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.extractsextracts_7_12_10}
                        </td>
                    </tr>
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top">
                        ब. संपादन करावयाच्या क्षेत्राचे ड्रोन‌द्वारे केलेले छायाचित्रण व KML फाईल जोडण्यात आले आहेत काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.drone_kml}
                        </td>
                    </tr>
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">11</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करावयाच्या क्षेत्राचे सीमांकन करण्यात आले आहे काय ? असल्यास, सीमांकन प्रमाणपत्र सोबत जोडले आहे काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.demarcation_11}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">12</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                  प्रस्तावामधील नमुद क्षेत्रासाठी यापूर्वीच थेट खरेदी / अन्य कायद्याच्या तरतुदीनुसार भूसंपादन प्रक्रिया सुरू करण्यात आलेली आहे काय ?(संपादनाबाबतचे प्रमाणपत्र)
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.acquisition_matter_12}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">13</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            क्र.12 चे उत्तर - होय असल्यास, संबंधित भूसंपादन कायदा व प्रस्तावित क्षेत्र नमूद करावे.
                        </td>
                        <td class="border border-black px-4 py-3">
                            <pre class="whitespace-pre-wrap break-words">${formData.land_acquisition_law_13 || "-"}</pre>
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">14</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे काय ? त्याबाबत प्रमाणपत्र सोबत जोडावे.
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.taba_14}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">15</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           सबंधित प्रयोजनासाठी जवळपास कमी खर्चाची अन्य खाजगी किंवा सरकारी जमीन उपलब्ध आहे काय ? असल्यास, किती अंतरावर आहे ? 
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.other_govt_land_15 || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">16</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करून मागितलेली जमीन रेल्वे सीमांच्या जवळपास आहे काय? असल्यास, किती अंतरावर आहे ?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.near_railway_boundaries_16 || "-"}
                        </td>
                    </tr>

           <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center" rowspan="2">17</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            अ. संपादन क्षेत्रात धार्मिक, प्रार्थना स्थळे, मंदिरे, कब्रस्थान व स्मशानभूमी किंवा पवित्र स्मारके येतात काय ?(प्रमाणपत्र)
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.religious_places_17 || "-"}
                        </td>
                    </tr>
                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            ब. संपादन क्षेत्राच्या जवळपास अशा बाबी असल्यास, किती अंतरावर आहेत ?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.proximity_distance || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">18</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनिःसारण मार्ग जातो काय ? किंवा खास काही अडथळा निर्माण होतो काय ? 
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.any_obstruction_18 || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">19</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाचे किंवा पूर्वापार प्रकाशमार्ग आणि / किंवा हायवेचे मार्ग यावरील कोणत्याही अधिकारात अडथळा येणेचा संभव आहे काय ?
                        </td>
                        <td class="border border-black px-4 py-3 break-words">
                            ${formData.road_obstruction_19 || "-"}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">20</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                           संपादन क्षेत्राच्या सभोवार इमारत किंवा कारखाना यांना किंवा त्यांची जागा छेदून जाते काय ?
                        </td>
                        <td class="border border-black px-4 py-3">
                            ${formData.private_buildings_20}
                        </td>
                    </tr>

                    <tr class="border border-black">
                        <td class="border border-black px-4 py-3 font-medium align-top text-center">21</td>
                        <td class="border border-black px-4 py-3 font-medium align-top">
                            प्रादेशिक विभाग/प्राधिकरण / महामंडळाच्या प्राधिकृत अधिकाऱ्याचा तपशील
                        </td>
                        <td class="border border-black px-4 py-3">
                            <div class="flex flex-col gap-2">
                                <div>
                                    <span class="font-semibold">नाव :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_name || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">पदनाम :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_designation || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">पत्ता :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_address || "-"}</span>
                                </div>
                            
                                <div>
                                    <span class="font-semibold">भ्रमणध्वनी :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_contact || "-"}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">कार्यालयीन दुरध्वनी :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_office_contact || "-"}</span>
                                </div>
                                    <div>
                                    <span class="font-semibold">ईमेल :</span>
                                    <span class="break-words">${formData.acquisition_regional_incharge_email || "-"}</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
              <div style="margin-top: 80px; display: flex; justify-content: space-between; font-size: 12px;">
        <div style="text-align: center; width: 45%;">
          
          <div>
            <div>(${formData.acquisition_regional_incharge_name || ''})</div>
            <div>${formData.acquisition_regional_incharge_designation || ''}</div>
          </div>
        </div>
        <div style="text-align: center; width: 45%;">
          
          <div>
            <div>(${formData.acquisition_incharge_name || ''})</div>
            <div>${formData.acquisition_incharge_designation || ''}</div>
          </div>
        </div>
      </div>
    </div>
  `;
    };

    const printHTML = `
    <!DOCTYPE html>
    <html>
    <head>
<title>नमुना "क" - ${projectName || ''} - ${formData.proposalId || ''}</title>      <meta charset="UTF-8">
      <style>
         @page {
          size: A4 portrait;   /* Orientation */
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
        }
        table {
          page-break-inside: auto;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        .border {
          border: 1px solid black;
        }
        .border-2 {
          border-width: 2px;
        }
        .border-black {
          border-color: black;
        }
        .border-collapse {
          border-collapse: collapse;
        }
        .w-full {
          width: 100%;
        }
        .text-xs {
          font-size: 0.75rem;
        }
        .text-sm {
          font-size: 0.875rem;
        }
        .text-base {
          font-size: 1rem;
        }
        .font-bold {
          font-weight: bold;
        }
        .font-medium {
          font-weight: 500;
        }
        .font-semibold {
          font-weight: 600;
        }
        .px-4 {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        .py-3 {
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        .mb-5 {
          margin-bottom: 1.25rem;
        }
        .pt-4 {
          padding-top: 1rem;
        }
        .text-center {
          text-align: center;
        }
        .align-top {
          vertical-align: top;
        }
        .break-words {
          word-wrap: break-word;
        }
        .leading-tight {
          line-height: 1.25;
        }
        .m-0 {
          margin: 0;
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .whitespace-pre-wrap {
          white-space: pre-wrap;
        }
        @media print {
          body { margin: 0; }
          button { display: none !important; }
          input[type="file"] { display: none !important; }
          select { display: none !important; }
          input { display: none !important; }
          textarea { display: none !important; }
        }
      </style>
    </head>
    <body>
      ${createPrintContent()}
    </body>
    </html>
  `;

    // Write the HTML and trigger print
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.focus();

    // Add a small delay before printing to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };


  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '16px'
      }}>
        प्रस्ताव माहिती लोड करत आहे...
      </div>
    );
  }

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      fontFamily: "Arial, sans-serif",
      maxWidth: "800px",
      margin: "0 auto"
    }}>
      {/* Header with back button */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        paddingBottom: "10px",
        borderBottom: "2px solid #2196F3"
      }}>
        <h2 style={{ margin: "0", color: "#2196F3" }}>
          प्रस्ताव संपादन
        </h2>
        {onBack && (
          <button
            onClick={onBack}
            className="no-print"
            style={{
              backgroundColor: "#757575",
              color: "white",
              padding: "8px 16px",
              border: "none",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer"
            }}
          >
            ← {translations[language]?.Back}
          </button>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: "flex",
        marginBottom: "20px"
      }}>
        <button
          onClick={handleDownload}
          style={{
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "10px 20px",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          📄{translations[language]?.Print}
        </button>
        {/* <input
          type="file"
          id="signatureUpload"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleSignatureUpload}
          style={{ display: "none" }}
        />
        <button>
          <label className="flex items-center space-x-2 px-4 py-2 ml-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 cursor-pointer">
            <Upload size={18} />
            <span>Upload Signature</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={handleUploadedFile}
            />
          </label>
        </button>

        <button
          onClick={handleViewSignedPdf}
          className="flex items-center space-x-2 px-4 py-2 ml-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
        >
          <Printer size={18} />
          <span>View Signed PDF</span>
        </button>

        {signatureFile && (
          <div style={{
            position: "absolute",
            top: "100%",
            left: "0",
            marginTop: "5px",
            padding: "5px 10px",
            backgroundColor: "#e8f5e8",
            border: "1px solid #4caf50",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#2e7d32",
            whiteSpace: "nowrap",
            zIndex: 10
          }}>
            ✓ {signatureFile.name}
          </div>
        )} */}
      </div>

      {/* Proposal Info */}
      <div style={{
        padding: "10px",
        borderRadius: "4px",
        marginBottom: "20px",
        fontSize: "12px", textAlign: "center",

      }}>
        <h2 style={{ marginBottom: "10px", fontSize: "18px", fontWeight: "bold" }}>
          नमुना "क"
        </h2>
        <div style={{ marginBottom: "12px" }}>
          <strong> प्रकल्प नाव:</strong> {projectName || "-"}
  {isEditingHeader ? (
    <div
      style={{
        display: "flex",
        gap: "8px",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <input
        type="text"
        value={formData.acquisition_proposal_no || ""}
        onChange={(e) =>
          handleInputChange("acquisition_proposal_no", e.target.value)
        }
        style={{
          width: "70%",
          border: "1px solid #ccc",
          padding: "6px 8px",
          fontSize: "12px",
          borderRadius: "4px",
        }}
      />

      <button
      type="button"
      onClick={handleHeaderSave}
      disabled={headerSaving}
      style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: "12px",
            }}
    >
      {headerSaving ? "Saving..." : "Save"}
    </button>

      <button
        type="button"
        onClick={() => setIsEditingHeader(false)}
        className="no-print"
        style={{
          backgroundColor: "#f44336",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          padding: "6px 12px",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        Cancel
      </button>
    </div>
  ) : (
    <div
      style={{
        display: "flex",
        gap: "8px",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <p className="text-xs m-0 leading-tight text-center">
      <strong> प्रस्ताव नाव :</strong> {formData.acquisition_proposal_no 
          }
      </p>

      <button
        type="button"
        onClick={() => setIsEditingHeader(true)}
        className="no-print"
        style={{
          backgroundColor: "#2196F3",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          padding: "4px 10px",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
         <Edit className="h-4 w-4" />
      </button>
    </div>
  )}
</div>
         
       </div>

      {/* Form Table */}
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        border: "2px solid #000",
        fontSize: "12px"
      }}>
        <thead>
          <tr>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "8%",
              textAlign: "center"
            }}>
              अनुक्रमांक
            </th>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "45%"
            }}>
              फील्ड नाव / Field Name
            </th>
            <th style={{
              border: "1px solid #000",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              width: "47%"
            }}>
              मूल्य / Value
            </th>
          </tr>
        </thead>
        <tbody>
          {formFields.map((field, idx) => (
            <tr key={idx}>
              {![10, 18].includes(idx) && (
                <td
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "center",
                    verticalAlign: "top"
                  }}
                  rowSpan={(idx === 9 || idx === 17) ? 2 : 1}
                >
                  {getSrNo(idx)}
                </td>
              )}
              <td style={{
                border: "1px solid #000",
                padding: "8px",
                verticalAlign: "top",
                fontWeight: "bold"
              }}>
                {field.label}
                {field.required && <span style={{ color: "red" }}> *</span>}
              </td>
              <td style={{
                border: "1px solid #000",
                padding: "8px",
                verticalAlign: "top"
              }}>
                {field.type === "text" && (
                  <input
                    type="text"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}

                {field.type === "dual_text" && (
                  <div>
                    <div style={{ marginBottom: "8px" }}>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field1Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field1Name] || ""}
                        onChange={(e) => handleInputChange(field.field1Name, e.target.value)}
                        placeholder={field.field1Placeholder}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field2Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field2Name] || ""}
                        onChange={(e) => handleInputChange(field.field2Name, e.target.value)}
                        placeholder={field.field2Placeholder}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field3Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field3Name] || ""}
                        onChange={(e) => handleInputChange(field.field3Name, e.target.value)}
                        placeholder={field.field3Placeholder}
                      />
                    </div>
                  </div>

                )}

                {field.type === "quad_text" && (
                  <div>
                    <div style={{ marginBottom: "8px" }}>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field1Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field1Name] || ""}
                        onChange={(e) => handleInputChange(field.field1Name, e.target.value)}
                        placeholder={field.field1Placeholder}
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field2Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field2Name] || ""}
                        onChange={(e) => handleInputChange(field.field2Name, e.target.value)}
                        placeholder={field.field2Placeholder}
                      />
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field3Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field3Name] || ""}
                        onChange={(e) => handleInputChange(field.field3Name, e.target.value)}
                        placeholder={field.field3Placeholder}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                        {field.field4Label}
                      </label>
                      <input
                        type="text"
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.field4Name] || ""}
                        onChange={(e) => handleInputChange(field.field4Name, e.target.value)}
                        placeholder={field.field4Placeholder}
                      />
                    </div>
                    {field.field5Name && (
                      <div style={{ marginBottom: "8px" }}>
                        <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                          {field.field5Label}
                        </label>
                        <input
                          type="text"
                          style={{
                            width: "100%",
                            border: "1px solid #ccc",
                            padding: "4px",
                            fontSize: "12px",
                            borderRadius: "2px"
                          }}
                          value={formData[field.field5Name] || ""}
                          onChange={(e) => handleInputChange(field.field5Name, e.target.value)}
                          placeholder={field.field5Placeholder}
                        />
                      </div>
                    )}
                    {field.field6Name && (
                      <div style={{ marginBottom: "8px" }}>
                        <label style={{ display: "block", fontSize: "10px", marginBottom: "2px", fontWeight: "bold" }}>
                          {field.field6Label}
                        </label>
                        <input
                          type="text"
                          style={{
                            width: "100%",
                            border: "1px solid #ccc",
                            padding: "4px",
                            fontSize: "12px",
                            borderRadius: "2px"
                          }}
                          value={formData[field.field6Name] || ""}
                          onChange={(e) => handleInputChange(field.field6Name, e.target.value)}
                          placeholder={field.field6Placeholder}
                        />
                      </div>
                    )}

                  </div>
                )}

                {field.type === "textarea" && (
                  <textarea
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      minHeight: "60px",
                      resize: "vertical",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                    rows={field.rows || 3}
                  />
                )}

                {field.type === "date" && (
                  <input
                    type="date"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formatDateForInput(formData[field.fieldName]) || ""}
                    onChange={(e) => {
                      // Store the date value directly from the date input
                      handleInputChange(field.fieldName, e.target.value);
                    }}
                  />
                )}

                {field.type === "select" && (
                  <>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <select
                        style={{
                          flex: "1",
                          border: "1px solid #ccc",
                          padding: "4px",
                          fontSize: "12px",
                          borderRadius: "2px"
                        }}
                        value={formData[field.fieldName] || ""}
                        onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                      >
                        <option value="">निवडा</option>
                        {field.options.map((option, i) => (
                          <option key={i} value={option.value}>{option.label}</option>
                        ))}
                      </select>

                      {/* View Button - Show when value is "हो" and file URL exists */}
                      {(
                        // For 12 and 17 → show button for both होय / नाही
                        (
                          ["acquisition_matter_12", "religious_places_17"].includes(field.fieldName) &&
                          ["होय", "नाही"].includes(formData[field.fieldName])
                        )
                        ||
                        // For all other fields → show only when "होय"
                        (
                          !["acquisition_matter_12", "religious_places_17"].includes(field.fieldName) &&
                          formData[field.fieldName] === "होय"
                        )
                      ) && formData[`${field.fieldName}_file`] && (
                          <button
                            type="button"
                            onClick={() => handleViewFile(field.fieldName)}
                            style={{
                              backgroundColor: "#4CAF50",
                              color: "white",
                              padding: "4px 8px",
                              border: "none",
                              borderRadius: "2px",
                              fontSize: "11px",
                              cursor: "pointer",
                              whiteSpace: "nowrap"
                            }}
                          >
                            👁️ पहा
                          </button>
                        )}

                      {field.type === "select" && (
                        <>
                          {/* FIXED: Show upload button if "हो" is selected and showUpload is true */}
                          {((field.showUpload === true && formData[field.fieldName] === "होय") ||
                            (field.showUpload === "both" && (formData[field.fieldName] === "होय" || formData[field.fieldName] === "नाही"))) && (
                              <div style={{ marginTop: "10px" }}>
                                <input
                                  type="file"
                                  onChange={(e) => handleFileUpload(e, field.fieldName)}
                                  accept={field.fieldName === "drone_kml"
                                    ? ".mp4,.kml,.kmz,video/mp4,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/xml"
                                    : ".pdf,.jpg,.jpeg,.png,.doc,.docx"}
                                  style={{ fontSize: "12px" }}
                                  required={!formData[`${field.fieldName}_file`]}
                                />
                                <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
                                  {field.fieldName === "drone_kml"
                                    ? "व्हिडिओ (MP4 - 20MB) किंवा KML/KMZ फाइल (5MB पर्यंत) अपलोड करणे आवश्यक आहे"
                                    : "कागदपत्र अपलोड करणे आवश्यक आहे"}
                                </div>
                                {formData[`${field.fieldName}_file`] && (
                                  <div style={{ fontSize: "10px", color: "green", marginTop: "2px" }}>
                                    ✓ अपलोड केले: {formData[`${field.fieldName}_file`].name}
                                  </div>
                                )}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </>
                )}
                {field.type === "number" && (
                  <input
                    type="number"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      padding: "4px",
                      fontSize: "12px",
                      borderRadius: "2px"
                    }}
                    value={formData[field.fieldName] || ""}
                    onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}

                {field.type === "static" && (
                  <div style={{
                    padding: "8px",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "pre-line"
                  }}>
                    {field.value}
                  </div>
                )}

                {field.type === "multiple_fields" && (
                  <div style={{ display: "grid", gap: "8px" }}>
                    {field.fields.map((subField, fieldIndex) => (
                      <div key={fieldIndex} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{
                          display: "block",
                          fontSize: "11px",
                          fontWeight: "bold",
                          marginBottom: "3px",
                          color: "#333"
                        }}>
                          {subField.label}:
                        </label>

                        {subField.type === "textarea" ? (
                          <textarea
                            style={{
                              width: "100%",
                              border: "1px solid #ddd",
                              outline: "none",
                              backgroundColor: "#fff",
                              padding: "6px",
                              fontSize: "12px",
                              borderRadius: "3px"
                            }}
                            rows={subField.rows || 2}
                            value={formData[subField.fieldName] || ""}
                            onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            placeholder={subField.placeholder}
                          />
                        ) 
                          : subField.type === "select" ? (
                            <select
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            >
                              <option value="">निवडा</option>
                              {subField.options.map((option, i) => (
                                <option key={i} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          ) : subField.type === "area_unit_dropdown" ? (
                            <select
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                            >
                              <option value="">क्षेत्रफळ युनिट निवडा</option>
                              {areaUnits.map((unit) => (
                                <option key={unit.area_unit_id} value={unit.unit_name}>
                                  {unit.unit_name} ({unit.unit_code})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={subField.type}
                              style={{
                                width: "100%",
                                border: "1px solid #ddd",
                                outline: "none",
                                backgroundColor: "#fff",
                                padding: "6px",
                                fontSize: "12px",
                                borderRadius: "3px"
                              }}
                              value={formData[subField.fieldName] || ""}
                              onChange={(e) => handleInputChange(subField.fieldName, e.target.value)}
                              placeholder={subField.placeholder}
                            />
                          )}
                      </div>
                    ))}
                  </div>
                )}

                {/* NEW DTV DISPLAY RENDER BLOCK */}
                {field.type === "dtv_display" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {formData.dtv && formData.dtv.length > 0 ? (
                      getFormattedDtv(formData.dtv).map((group, i) => (
                        <div key={i} style={{
                          padding: "8px",
                          // backgroundColor: "#f9f9f9",
                          // border: "1px solid #ddd",
                          // borderRadius: "4px",
                          fontSize: "12px"
                        }}>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>जिल्हा:</strong> {group.district}
                          </div>
                          <div style={{ marginBottom: "4px" }}>
                            <strong>तालुका:</strong> {group.taluka}
                          </div>
                          <div>
                            <strong>गाव:</strong> {group.villages}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "8px", color: "#666" }}>
                        माहिती उपलब्ध नाही (No Data Available)
                      </div>
                    )}
                  </div>
                )}

              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Save Button */}
      <div style={{
        textAlign: "center",
        marginTop: "20px",
        marginBottom: "20px"
      }}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="no-print"
          style={{
            backgroundColor: saving ? "#ccc" : "#2196F3",
            color: "white",
            padding: "12px 24px",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: "bold"
          }}
        >
          {saving ? <span>{translations[language]?.Saving}</span> : <span>{translations[language]?.Save}</span>}
        </button>
      </div>

      {/* Footer Signatures */}
      <div style={{
        marginTop: "40px",
        display: "flex",
        justifyContent: "space-between",
        fontSize: "12px"
      }}>
        <div style={{ textAlign: "center", width: "45%" }}>
          {/* <div style={{ height: "60px", borderBottom: "1px solid #000", marginBottom: "5px" }}></div> */}
          <div>
            <div>{formData.acquisition_regional_incharge_name || ''}</div>
            <div>{formData.acquisition_regional_incharge_designation || ''}</div>
          </div>
        </div>
        <div style={{ textAlign: "center", width: "45%" }}>
          <div>
            <div>{formData.acquisition_incharge_name || ''}</div>
            <div>{formData.acquisition_incharge_designation || ''}</div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {translations[language]?.DocumentPreview}
              </h3>
              <button
                onClick={() => {
                  console.log("❌ Closing modal, current documentUrl:", documentUrl);
                  setShowModal(false);
                  if (documentUrl?.startsWith("blob:")) {
                    console.log("🧹 Revoking blob URL:", documentUrl);
                    URL.revokeObjectURL(documentUrl);
                  }
                }}
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Document Viewer */}
            {console.log("🖼 Rendering iframe with src:", documentUrl)}
            <iframe
              src={documentUrl}
              className="w-full h-[80vh] rounded border border-gray-300"
              title="Document Preview"
            />
          </div>
        </div>
      )}

      {showSignaturePad && pdfForSigning && (
        <SignaturePad
          pdfFile={pdfForSigning}
          onSign={async (signaturesArray) => {
            setShowSignaturePad(false);
            await signDocument(pdfForSigning, signaturesArray);
            setPdfForSigning(null);
          }}
          onCancel={() => {
            setShowSignaturePad(false);
            setPdfForSigning(null);
          }}
        />
      )}

      {isViewerOpen && viewerFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg w-[80%] max-w-6xl h-[80%] relative">

            <div className="flex justify-between items-center px-4 py-2 border-b">
              <h3 className="text-lg font-semibold">Signed Document</h3>
              <button
                onClick={() => setIsViewerOpen(false)}
                className="text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <iframe
              src={viewerFile}
              title="Signed PDF Viewer"
              className="w-full h-full"
            />
          </div>
        </div>
      )}



    </div>

  );
};

// Form fields configuration
const formFields = [
  {
    label: "भूसंपादन प्रस्ताव पाठविणाऱ्या अधिका-याचे नाव,पदनाम व पत्ता",
    type: "dual_text",
    field1Name: "acquisition_incharge_name",
    field1Label: "नाव",
    field1Placeholder: "अधिकाऱ्याचे संपूर्ण नाव",
    field2Name: "acquisition_incharge_designation",
    field2Label: "पदनाम",
    field2Placeholder: "नाव व पदनाम ",
    field3Name: "acquisition_incharge_address",
    field3Label: "पत्ता",
    field3Placeholder: "नाव व पत्ता   ",
  },
  //2
  {
    label: "भूसंपादन प्रस्ताव सादर करणाऱ्या प्रादेशिक विभागाचे / प्राधिकरणाचे / महामंडळाचे नाव व पत्ता",
    type: "text",
    fieldName: "acquisition_agency",
    placeholder: "नाव व पत्ता"
  },
  //3
  {
    label: "मंत्रालयीन स्तरावरील संबंधीत प्रशासकीय विभाग",
    type: "text",
    fieldName: "acquisition_department",
    placeholder: "विभागाचे नाव"

  },
  //4
  {
    label: "जमीन कोणत्या कारणासाठी संपादन करून हवी आहे ?",
    type: "text",
    fieldName: "acquisition_purpose"
  },
  //5
  {
    label: "जमीन कोणत्या शासन धोरणानुसार/कायद्यानुसार संपादन करून हवी आहे ?",
    type: "textarea",
    fieldName: "acquisition_government_policy",
    placeholder: "संस्थेचे संपूर्ण नाव व पत्ता",
    rows: 3
  },
  // 6 - UPDATED TO DTV DISPLAY
  {
    label: "संपादन करावयाच्या जमिनी ज्या क्षेत्रात मोडतात ते गाव, तालुका, जिल्हा.",
    type: "dtv_display", 
    fieldName: "dtv"
  },
  //7
  {
    label: "संपादन करून हवे असलेले एकूण क्षेत्र",
    type: "multiple_fields",
    fields: [
      {
        label: "एकूण क्षेत्र",
        fieldName: "total_area",
        type: "text",
        placeholder: "एकूण क्षेत्र प्रविष्ट करा"
      },
      {
        label: "संपादन क्षेत्र",
        fieldName: "aquired_area", // fixed spelling
        type: "text",
        placeholder: "संपादन क्षेत्र प्रविष्ट करा"
      },
      {
        label: "क्षेत्रफळ युनिट",
        fieldName: "area_unit",
        type: "area_unit_dropdown"
      }
    ]
  },
  //8
  {
    label: "गावनिहाय व गट/सर्व्हे क्र. नुसार आणि संपादन क्षेत्राच्या चतुःसीमा दाखविणारी अनुसूची यासोबत जोडणे आवश्यक आहे, त्याप्रमाणे ती जोडली आहे काय ?(परिशिष्ट 6)",
    type: "select",
    fieldName: "schedule_6_08",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //9
  {
    label: "संपादन करावयाच्या क्षेत्राचा चिन्हांकित / साक्षांकित नकाशा प्रस्तावासोबत  जोडणे आवश्यक आहे, त्या प्रमाणे जोडला आहे काय ? भूमिअभिलेख विभागाकडील गावाचे नकाशे दोन प्रतीत सादर केले आहे काय ?",
    type: "select",
    fieldName: "schedule_4_09",
    placeholder: "गावाचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]

  },
  {
    label: "अ. संपादन करावयाच्या क्षेत्राचे ७/१२ चे उतारे किंवा मालमत्ता नोंदवहीतील उतारा प्रस्तावासोबत जोडणे आवश्यक आहे, त्याप्रमाणे जोडण्यात आले आहेत काय ?",
    type: "select",
    fieldName: "extractsextracts_7_12_10",
    placeholder: "तालुक्याचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  // 10b
  {
    label: "ब. संपादन करावयाच्या क्षेत्राचे ड्रोन‌द्वारे केलेले छायाचित्रण व KML फाईल जोडण्यात आले आहेत काय ?",
    type: "select",
    fieldName: "drone_kml", // <-- NOTE: This is a new, unique field name for 10b
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //11
  {
    label: "संपादन करावयाच्या क्षेत्राचे सीमांकन करण्यात आले आहे काय ? असल्यास, सीमांकन प्रमाणपत्र सोबत जोडले आहे काय ?",
    type: "select",
    fieldName: "demarcation_11",
    placeholder: "जिल्ह्याचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //12
  {
    label: "प्रस्तावामधील नमुद क्षेत्रासाठी यापूर्वीच थेट खरेदी / अन्य कायद्याच्या तरतुदीनुसार भूसंपादन प्रक्रिया सुरू करण्यात आलेली आहे काय ?(संपादनाबाबतचे प्रमाणपत्र)",
    type: "select",
    fieldName: "acquisition_matter_12",
    showUpload: "both",
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //13
  {
    label: "क्र.12 चे उत्तर - होय असल्यास, संबंधित भूसंपादन कायदा व प्रस्तावित क्षेत्र नमूद करावे.",
    type: "text",
    fieldName: "land_acquisition_law_13",
    placeholder: "एकूण क्षेत्र"
  },
  //14
  {
    label: "गरजेनुसार आवश्यक तेवढेच क्षेत्र संपादन करून मागितले आहे काय ? त्याबाबत प्रमाणपत्र सोबत जोडावे.",
    type: "select",
    fieldName: "taba_14",
    placeholder: "प्रादेशिक अधिकाऱ्याचे नाव",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //15
  {
    label: "सबंधित प्रयोजनासाठी जवळपास कमी खर्चाची अन्य खाजगी किंवा सरकारी जमीन उपलब्ध आहे काय ? असल्यास, किती अंतरावर आहे ?",
    type: "select",
    fieldName: "other_govt_land_15",
    placeholder: "पदनाम",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //16
  {
    label: "संपादन करून मागितलेली जमीन रेल्वे सीमांच्या जवळपास आहे काय? असल्यास, किती अंतरावर आहे ?",
    type: "select",
    fieldName: "near_railway_boundaries_16",
    placeholder: "ईमेल पत्ता",
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },

  //17
  {
    label: "अ.संपादन क्षेत्रात धार्मिक, प्रार्थना स्थळे, मंदिरे, कब्रस्थान व स्मशानभूमी किंवा पवित्र स्मारके येतात काय ?",
    type: "select",
    fieldName: "religious_places_17",
    showUpload: "both",
    options: [
      { label: "होय", value: "होय" },
      { label: "नाही", value: "नाही" }
    ]
  },
  {
    label: " ब.संपादन क्षेत्राच्या जवळपास अशा बाबी असल्यास, किती अंतरावर आहेत ?",
    type: "text",
    fieldName: "proximity_distance",
    placeholder: "मीटर / किलोमीटर मध्ये लिहा"
  },

  //18
  {
    label: " संपादन करावयाच्या क्षेत्रातून कोणताही सार्वजनिक मार्गाधिकार, जलप्रवाह, जलवाहिनी किंवा जलनिःसारण मार्ग जातो काय ? किंवा खास काही अडथळा निर्माण होतो काय ?",
    type: "select",
    fieldName: "any_obstruction_18",
    placeholder: "प्रकल्पाचे तपशीलवार वर्णन",
    rows: 4,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //19
  {
    label: "नागरी क्षेत्रात इमारतीमधील खाजगी प्रवेश मार्गाचे किंवा पूर्वापार प्रकाशमार्ग आणि / किंवा हायवेचे मार्ग यावरील कोणत्याही अधिकारात अडथळा येणेचा संभव आहे काय ?",
    type: "select",
    fieldName: "road_obstruction_19",
    placeholder: "सामाजिक फायद्याचे वर्णन",
    rows: 3,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },
  //20
  {
    label: "संपादन क्षेत्राच्या सभोवार इमारत किंवा कारखाना यांना किंवा त्यांची जागा छेदून जाते काय ?",
    type: "select",
    fieldName: "private_buildings_20",
    placeholder: "विस्थापन का आवश्यक आहे",
    rows: 2,
    showUpload: true,
    options: [
      { value: "होय", label: "होय" },
      { value: "नाही", label: "नाही" }
    ]
  },

  //21
  {
    label: "प्रादेशिक विभाग/प्राधिकरण / महामंडळाच्या प्राधिकृत अधिकाऱ्याचा तपशील",
    type: "quad_text",
    field1Name: "acquisition_regional_incharge_name",
    field1Label: "नाव",
    field1Placeholder: "नाव",
    field2Name: "acquisition_regional_incharge_designation",
    field2Label: "पदनाम",
    field2Placeholder: "पदनाम",
    field3Name: "acquisition_regional_incharge_address",
    field3Label: "पत्ता",
    field3Placeholder: "पत्ता",
    field4Name: "acquisition_regional_incharge_contact",
    field4Label: "भ्रमणध्वनी",
    field4Placeholder: "भ्रमणध्वनी",
    field5Name: "acquisition_regional_incharge_office_contact",
    field5Label: "कार्यालयीन दुरध्वनी क्र.",
    field5Placeholder: "कार्यालयीन दुरध्वनी क्र.",
    field6Name: "acquisition_regional_incharge_email",
    field6Label: "ई-मेल",
    field6Placeholder: "ई-मेल",
  }
];

export default EditProposal;